import { Loader2, PlayCircle } from "lucide-react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  /** Poster shown behind the overlay when there is no live video yet. */
  previewUrl?: string;
  /** Disables the button and swaps in the spinner while connecting. */
  busy?: boolean;
  onStart: () => void;
  onBack?: () => void;
  errorMessage?: string | null;
}

/**
 * Shared "Start" gate rendered on top of the avatar frame. Every provider
 * uses this once the pre-session configuration step is done (or skipped by
 * auto-start), so the learner always confirms before the session goes live.
 */
export function SessionStartOverlay({ previewUrl, busy, onStart, onBack, errorMessage }: Props) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-accent overflow-hidden">
      {previewUrl && (
        <img src={previewUrl} className="h-full w-full object-cover opacity-80" alt="" />
      )}
      {onBack && (
        <Button
          onClick={onBack}
          size="icon"
          variant="secondary"
          className="absolute top-4 left-4 rounded-lg bg-card/70 backdrop-blur z-10"
          aria-label="Back"
          disabled={busy}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
      )}
      <div className="absolute inset-0 flex items-center justify-center bg-background/20">
        <Button
          onClick={onStart}
          size="lg"
          className="h-14 px-8 rounded-full shadow-2xl text-base font-semibold"
          disabled={busy}
        >
          {busy ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin" />
              Starting…
            </>
          ) : (
            <>
              <PlayCircle className="h-6 w-6" />
              Start
            </>
          )}
        </Button>
      </div>
      {errorMessage && (
        <div className="absolute bottom-0 left-0 right-0 bg-destructive px-4 py-2 text-center">
          <p className="text-xs text-destructive-foreground">{errorMessage}</p>
        </div>
      )}
    </div>
  );
}
